import { useEffect, useState } from 'react';
import './App.css';
import useGame from './Engine/useGame';
import usePlayer from './Engine/usePlayer';
import useObstacle from './Engine/useObtacle';
import Background from './View/Background';
import InfoBar from './View/InfoBar';
import useSky from './Engine/useSky';
import Menu from './View/Menu';
import PauseMenu from './View/PauseMenu';
import GameOverMenu from './View/GameOverMenu';

// Game states
const GAME_STATE = {
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAME_OVER: 'gameover'
};

function App() {
  const [gameState, setGameState] = useState(GAME_STATE.MENU);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('dashcube_highscore');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [currentScore, setCurrentScore] = useState(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);

  const player = usePlayer(50, 50);
  const [obstacles, addObstacle, resetObs] = useObstacle();
  const game = useGame();
  const sky = useSky();
  const paddingLeft = 80;

  // Start game
  const handleStartGame = () => {
    setGameState(GAME_STATE.PLAYING);
    setIsNewHighScore(false);
    player.run();
  };

  // Pause game
  const handlePause = () => {
    if (gameState === GAME_STATE.PLAYING) {
      player.stopRun();
      setGameState(GAME_STATE.PAUSED);
    }
  };

  // Resume game
  const handleResume = () => {
    setGameState(GAME_STATE.PLAYING);
    player.run();
  };

  // Return to main menu
  const handleMainMenu = () => {
    player.stopRun();
    resetObs();
    player.restart();
    setGameState(GAME_STATE.MENU);
  };

  // Restart game
  const handleRestart = () => {
    resetObs();
    player.restart();
    setIsNewHighScore(false);
    setGameState(GAME_STATE.PLAYING);
    player.run();
  };

  // Game over handler
  const handleGameOver = (score) => {
    player.stopRun();
    setCurrentScore(score);
    
    if (score > highScore) {
      setHighScore(score);
      setIsNewHighScore(true);
      localStorage.setItem('dashcube_highscore', score.toString());
    }
    
    setGameState(GAME_STATE.GAME_OVER);
  };

  // Listen for collisions
  useEffect(() => {
    if (gameState !== GAME_STATE.PLAYING) return;
    
    game.listen(player, obstacles, resetObs, handleGameOver);
  }, [gameState, player, obstacles, game, resetObs]);

  // Add obstacles
  useEffect(() => {
    if (gameState !== GAME_STATE.PLAYING) return;
    
    if (player.x > obstacles[obstacles.length - 3].x) addObstacle();
  }, [gameState, player.x, obstacles, addObstacle]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Pause/Resume with ESC
      if (e.key === 'Escape') {
        if (gameState === GAME_STATE.PLAYING) {
          handlePause();
        } else if (gameState === GAME_STATE.PAUSED) {
          handleResume();
        }
        return;
      }

      // Game controls (only when playing)
      if (gameState === GAME_STATE.PLAYING) {
        if (e.key === 'ArrowUp' || e.key === ' ' || e.key === 'w' || e.key === 'W') {
          e.preventDefault();
          if (!player.isRunning) player.run();
          else if (!player.isJumping) player.jump();
        }
        if (e.key === 'ArrowRight') {
          if (!player.isRunning) player.run();
        }
      }
    };

    const handleClick = () => {
      if (gameState === GAME_STATE.PLAYING) {
        if (!player.isRunning) player.run();
        else if (!player.isJumping) player.jump();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.removeEventListener('click', handleClick);
    };
  }, [gameState, player]);

  return (
    <>
      <div className="canvas" style={{
        backgroundImage: sky.bg
      }}>
        <div className='player' onClick={() => player.jump()} style={{
          bottom: player.y,
          left: paddingLeft,
          height: player.height,
          width: player.width,
          opacity: player.opacity + '%'
        }}>
        </div>
        {obstacles.length > 0 && obstacles.map((obs, key) =>
          <div className='obstacle' key={key} style={{
            left: obs.x - player.x + paddingLeft,
            bottom: 0,
            height: obs.h,
            width: obs.w,
            backgroundColor: obs.color
          }}>
          </div>
        )}
        <Background player_x={player.x} />
        <InfoBar player={player} game={{ ...game, highscore: highScore }} />
      </div>
      <div className='floor'></div>

      {/* Menus */}
      {gameState === GAME_STATE.MENU && (
        <Menu onStart={handleStartGame} highScore={highScore} />
      )}
      
      {gameState === GAME_STATE.PAUSED && (
        <PauseMenu onResume={handleResume} onMainMenu={handleMainMenu} />
      )}
      
      {gameState === GAME_STATE.GAME_OVER && (
        <GameOverMenu 
          score={currentScore}
          highScore={highScore}
          isNewHighScore={isNewHighScore}
          onRestart={handleRestart}
          onMainMenu={handleMainMenu}
        />
      )}
    </>
  );
}

export default App;
